import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from "react";
import { cn } from "~/lib/cn";

type Variant = "primary" | "ghost" | "dark-glass";

const base =
  "inline-flex items-center justify-center gap-2 rounded-[var(--radius-pill)] px-6 py-3 text-sm font-medium tracking-[-0.005em] transition-all duration-[var(--duration-micro)] ease-[var(--ease-prism)]";

const variants: Record<Variant, string> = {
  primary:
    "bg-coral text-white hover:bg-coral-700 shadow-[0_1px_0_rgba(255,255,255,0.18)_inset,0_12px_40px_-12px_rgba(207,110,84,0.45)]",
  ghost:
    "bg-transparent text-glacial-navy hover:bg-glacial-ice",
  "dark-glass":
    "glass-dark text-[color:var(--on-navy)] hover:shadow-[0_0_0_1px_var(--color-glacial-cyan)_inset]",
};

type SharedProps = {
  variant?: Variant;
  className?: string;
  children: ReactNode;
};

export function Button({
  variant = "primary",
  className,
  children,
  href,
  ...rest
}: SharedProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "children"> & {
    href?: string;
  }) {
  const classes = cn(base, variants[variant], className);
  if (href) {
    return (
      <a className={classes} href={href} {...(rest as AnchorHTMLAttributes<HTMLAnchorElement>)}>
        {children}
      </a>
    );
  }
  return (
    <button className={classes} {...(rest as ButtonHTMLAttributes<HTMLButtonElement>)}>
      {children}
    </button>
  );
}
